import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { Field, FieldError, FieldGroup, FieldLabel } from '~/shadcn/ui/field'
import { Form, FormField, FormItem } from '~/shadcn/ui/form/form'
import { Spinner } from '~/shadcn/ui/spinner'
import { Input } from '~/shadcn/ui/input'
import { LoginWithGoogleButton } from '~/@pages_auth/google-login/login-with-google-button/LoginWithGoogleButton'
import { loginSchema, type LoginSchema } from './loginSchemas'
import type { LoginRequest } from './useLoginMutation'
import { HeadingTwo } from '~/@mid_ui/typography/HeadingTwo'
import { Button } from '~/shadcn/ui/button/button'
import { isSocialNetworkInAppWebView } from '~/@lib/utils/function.utils'

type LoginFormProps<T> = {
  handleSubmitForm: (props: T) => void
  isRequesting: boolean
  error: string | null
}

function LoginForm(props: LoginFormProps<LoginRequest>) {
  const form = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  return (
    <div className="px-5 pt-10 pb-20">
      <Field className="flex flex-col items-start">
        <HeadingTwo className="font-bold mb-10">Prihlásenie</HeadingTwo>
      </Field>
      {!isSocialNetworkInAppWebView() && (
        <Field className="flex flex-col items-start gap-4 mb-10">
          <LoginWithGoogleButton />
        </Field>
      )}

      <Field className="flex flex-col items-start gap-15 mb-10">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(props.handleSubmitForm)}>
            <FieldGroup>
              <FormField
                control={form.control}
                name="email"
                render={({ fieldState, field }) => (
                  <FormItem>
                    <FieldLabel htmlFor="email">Váš e-mail</FieldLabel>
                    <Input
                      aria-invalid={!!fieldState.error}
                      id="email"
                      type="text"
                      autoComplete="off"
                      {...field}
                    />
                    {fieldState.error && (
                      <FieldError>{fieldState.error.message}</FieldError>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ fieldState, field }) => (
                  <FormItem>
                    <div className="flex items-center">
                      <FieldLabel htmlFor="password">Heslo</FieldLabel>
                    </div>
                    <Input
                      aria-invalid={!!fieldState.error}
                      id="password"
                      type="password"
                      autoComplete="off"
                      {...field}
                    />
                    {fieldState.error && (
                      <FieldError>{fieldState.error.message}</FieldError>
                    )}
                  </FormItem>
                )}
              />

              <div className="inline-flex flex-row items-center justify-center gap-2">
                <Link
                  to="/forgot-password"
                  className="text-accent hover:underline">
                  Zabudli ste heslo?
                </Link>
              </div>

              <Field className="mt-4">
                <Button
                  variant="accent"
                  type="submit"
                  size="lg"
                  disabled={props.isRequesting}>
                  {props.isRequesting ? (
                    <Spinner className="size-4" />
                  ) : (
                    'Prihlásenie'
                  )}
                </Button>
              </Field>

              {form.formState.errors.root && (
                <FieldError className="text-sm font-medium text-destructive">
                  {form.formState.errors.root.message}
                </FieldError>
              )}

              {props.error != null && (
                <FieldError className="text-sm font-medium text-destructive">
                  {props.error}
                </FieldError>
              )}
            </FieldGroup>
          </form>

          <div className="inline-flex flex-row items-center justify-center gap-2">
            Nie ste registrovaný?{' '}
            <Link to="/register" className="text-accent hover:underline">
              Zaregistrujte sa!
            </Link>
          </div>
        </Form>
      </Field>
    </div>
  )
}

export { type LoginFormProps, LoginForm }
